import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Link as RouterLink,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  Typography,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";

import ConfirmAction from "../../Tools/ConfirmAction";
import {
  loadRuleCatalog,
  selectRuleCatalog,
  selectRuleTypesByKey,
} from "../../../Redux/Reducers/ruleCatalogReducer";
import {
  auditSentence,
  roundCounts,
  rulesApi,
  schemaFor,
  schemaRounds,
} from "../../../utils/rulesApi";
import { defaultOrder } from "../../../utils/ruleDraft";
import { Breadcrumbs, rulesPath, StatusChip } from "../common";
import { useRuleStyles } from "../styles";
import { keepState, resolveReturnTo } from "../../../utils/returnTo";
import AddRuleDialog, { MODES } from "../AddRuleDialog";
import CopySetDialog from "../RuleSets/CopySetDialog";
import ClassificationToggles from "./ClassificationToggles";
import HeaderForm, { MAX_PER_KIND, MAX_TOTAL } from "./HeaderForm";
import RuleList from "./RuleList";

const DIVE_COUNT_TYPE = "DiveCount";

export const newHeader = () => ({
  ruleName: "",
  ageGroup: "",
  gender: "Any",
  height: "",
  rounds: "oooooo",
  storedSchema: null,
  novice: false,
  synchro: false,
  masters: false,
  code: "",
  status: "ACTIVE",
});

export const headerFromSet = (set) => ({
  ruleName: set.ruleName || "",
  ageGroup: set.ageGroup || "",
  gender: set.gender || "Any",
  height: set.height == null ? "" : String(set.height),
  rounds:
    schemaRounds(set.jumpSchema) ??
    "v".repeat(set.voluntaries || 0) + "o".repeat(set.optionals || 0),
  storedSchema: set.jumpSchema || null,
  novice: Boolean(set.novice),
  synchro: Boolean(set.synchro),
  masters: Boolean(set.masters),
  code: set.code == null ? "" : String(set.code),
  status: set.status || "ACTIVE",
});

let rowSeq = 0;
const nextKey = () => {
  rowSeq += 1;
  return `row-${rowSeq}`;
};

const rowFromSetRule = (r) => ({
  key: nextKey(),
  linkId: r.linkId ?? null,
  ruleId: r.ruleId,
  draft: null,
  rule: null,
  type: r.type,
  category: r.category,
  text: r.text,
  usageCount: r.usageCount || 0,
});

export const validateHeader = (header) => {
  const errors = {};
  if (!header.ruleName.trim()) errors.ruleName = "Rule set name is required";
  else if (header.ruleName.trim().length > 255)
    errors.ruleName = "At most 255 characters";
  if (!header.ageGroup.trim()) errors.ageGroup = "Age group is required";
  else if (header.ageGroup.trim().length > 32)
    errors.ageGroup = "At most 32 characters";
  if (!header.gender) errors.gender = "Gender is required";
  if (!header.height) errors.height = "Height is required";
  if (!/^[0-9]+$/.test(header.code)) errors.code = "Code must be digits only";
  const total = header.rounds.length;
  const { voluntaries, optionals } = roundCounts(header.rounds);
  if (total < 1 || total > MAX_TOTAL) {
    errors.total = `Total dives must be between 1 and ${MAX_TOTAL}`;
  } else if (voluntaries > MAX_PER_KIND || optionals > MAX_PER_KIND) {
    errors.total = `At most ${MAX_PER_KIND} voluntaries and ${MAX_PER_KIND} optionals`;
  }
  return errors;
};

export const buildUpsertBody = (ruleGroupId, header, rows) => ({
  ruleGroupId: Number(ruleGroupId),
  ruleName: header.ruleName.trim(),
  ageGroup: header.ageGroup.trim(),
  gender: header.gender,
  height: header.height,
  jumpSchema: schemaFor(header.rounds, header.storedSchema),
  novice: Boolean(header.novice),
  synchro: Boolean(header.synchro),
  masters: Boolean(header.masters),
  code: header.code,
  status: header.status,
  rules: rows.map((row) =>
    row.ruleId ? { ruleId: row.ruleId } : { draft: row.draft },
  ),
});

/**
 * Screen 3: one rule set. `/rules/groups/:groupId/sets/new` (optionally `?copyFrom=<setId>`)
 * creates; `/rules/sets/:setId` edits. The pinned "number of dives" rule is never sent: the
 * server keeps it in step with the schema.
 */
const RuleSetEditor = () => {
  const classes = useRuleStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { groupId: routeGroupId, setId } = useParams();
  const isNew = !setId;
  const copyFrom = useMemo(
    () => new URLSearchParams(location.search).get("copyFrom"),
    [location.search],
  );

  const catalog = useSelector(selectRuleCatalog);
  const typesByKey = useSelector(selectRuleTypesByKey);

  const [group, setGroup] = useState(null);
  const [detail, setDetail] = useState(null);
  const [header, setHeader] = useState(newHeader);
  const [rows, setRows] = useState([]);
  const [pinned, setPinned] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [warnings, setWarnings] = useState([]);
  const [dialog, setDialog] = useState({
    open: false,
    mode: MODES.ADD,
    initial: null,
    rowKey: null,
  });
  const [copying, setCopying] = useState(false);
  const [statusConfirm, setStatusConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const pinnedRequest = useRef(0);

  const ruleGroupId = detail?.ruleGroupId ?? routeGroupId;
  const total = header.rounds.length;

  useEffect(() => {
    dispatch(loadRuleCatalog());
  }, [dispatch]);

  // ---- load ------------------------------------------------------------------------------
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    setSaveError(null);
    setDirty(false);
    const run = async () => {
      if (!isNew) {
        const set = await rulesApi.set(setId);
        if (cancelled) return;
        setDetail(set);
        setHeader(headerFromSet(set));
        setRows((set.rules || []).filter((r) => !r.pinned).map(rowFromSetRule));
        const pinnedRule = (set.rules || []).find((r) => r.pinned);
        setPinned(
          pinnedRule
            ? { text: pinnedRule.text, usageCount: pinnedRule.usageCount }
            : null,
        );
        setWarnings(set.warnings || []);
        setGroup(await rulesApi.group(set.ruleGroupId));
        return;
      }
      setDetail(null);
      setWarnings([]);
      const [grp, source] = await Promise.all([
        rulesApi.group(routeGroupId),
        copyFrom ? rulesApi.set(copyFrom) : Promise.resolve(null),
      ]);
      if (cancelled) return;
      setGroup(grp);
      if (source) {
        setHeader({
          ...headerFromSet(source),
          ruleName: `${source.ruleName} (copy)`,
          code: "",
          status: "ACTIVE",
        });
        setRows(
          (source.rules || []).filter((r) => !r.pinned).map(rowFromSetRule),
        );
        setPinned(null);
        setDirty(true);
      } else {
        setHeader(newHeader());
        setRows([]);
        setPinned(null);
      }
    };
    run()
      .catch((e) => !cancelled && setLoadError(e.message))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [isNew, setId, routeGroupId, copyFrom]);

  // ---- pinned "number of dives" row follows the schema --------------------------------------
  useEffect(() => {
    if (loading) return undefined;
    const stored = (detail?.rules || []).find((r) => r.pinned);
    if (stored && detail.totalDives === total) {
      setPinned({ text: stored.text, usageCount: stored.usageCount });
      return undefined;
    }
    if (total < 1 || total > MAX_TOTAL) {
      setPinned(null);
      return undefined;
    }
    const id = pinnedRequest.current + 1;
    pinnedRequest.current = id;
    const handle = setTimeout(() => {
      rulesApi
        .preview({ type: DIVE_COUNT_TYPE, diveCount: total })
        .then((data) => {
          if (pinnedRequest.current !== id) return;
          setPinned({
            text: data?.text || "",
            usageCount: data?.existingUsageCount || 0,
          });
        })
        .catch(() => {
          if (pinnedRequest.current === id)
            setPinned({ text: "", usageCount: 0 });
        });
    }, 300);
    return () => clearTimeout(handle);
  }, [loading, detail, total]);

  // ---- unsaved changes --------------------------------------------------------------------
  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (event) => {
      event.preventDefault();
      // eslint-disable-next-line no-param-reassign
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  const leave = () => {
    // eslint-disable-next-line no-alert
    if (!dirty || window.confirm("Discard unsaved changes?")) {
      navigate(resolveReturnTo(location, rulesPath.sets(ruleGroupId)));
    }
  };

  const touch = (fn) => (value) => {
    fn(value);
    setDirty(true);
  };

  const updateHeader = touch(setHeader);
  const updateRows = touch(setRows);

  // ---- rules ------------------------------------------------------------------------------
  const openAdd = () =>
    setDialog({ open: true, mode: MODES.ADD, initial: null, rowKey: null });

  const openEdit = async (row, mode) => {
    let rule = row.rule;
    if (row.ruleId && !rule) {
      try {
        rule = await rulesApi.rule(row.ruleId);
      } catch (e) {
        toast.error(e.message);
        return;
      }
    }
    setDialog({
      open: true,
      mode,
      rowKey: row.key,
      initial: {
        ruleId: row.ruleId || null,
        type: row.type,
        rule: rule || null,
        draft: row.draft || null,
        usageCount: row.usageCount || 0,
      },
    });
  };

  const applyDialog = (result) => {
    const { rowKey, mode } = dialog;
    setDialog({ open: false, mode: MODES.ADD, initial: null, rowKey: null });
    if (mode === MODES.ADD) {
      updateRows([...rows, { key: nextKey(), linkId: null, ...result }]);
      return;
    }
    const index = rows.findIndex((row) => row.key === rowKey);
    if (index < 0) return;
    const previous = rows[index];
    const next = [...rows];
    next[index] = { ...previous, ...result, key: previous.key };
    // Same stored rule (edited in place or everywhere): the set's links did not change.
    const linkChanged = result.ruleId !== previous.ruleId || !result.ruleId;
    if (linkChanged) updateRows(next);
    else setRows(next);
  };

  const removeRow = (row) => updateRows(rows.filter((r) => r.key !== row.key));

  // ---- save ---------------------------------------------------------------------------------
  const headerErrors = useMemo(() => validateHeader(header), [header]);
  const canSave =
    Object.keys(headerErrors).length === 0 && !saving && (isNew || dirty);

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    setSaveError(null);
    const body = buildUpsertBody(ruleGroupId, header, rows);
    try {
      const saved = isNew
        ? await rulesApi.createSet(body)
        : await rulesApi.updateSet(setId, body);
      toast.success(isNew ? "Rule set created" : "Rule set saved");
      setDirty(false);
      setWarnings(saved.warnings || []);
      if (isNew) {
        navigate(rulesPath.set(saved.id), {
          replace: true,
          state: keepState(location),
        });
      } else {
        setDetail(saved);
        setHeader(headerFromSet(saved));
        setRows(
          (saved.rules || []).filter((r) => !r.pinned).map(rowFromSetRule),
        );
      }
    } catch (e) {
      setSaveError({ message: e.message, ...(e.details || {}) });
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async () => {
    const next = header.status === "DEPRECATED" ? "ACTIVE" : "DEPRECATED";
    setBusy(true);
    try {
      const saved = await rulesApi.setStatus(setId, next);
      setDetail((prev) => ({
        ...(prev || {}),
        ...saved,
        rules: prev?.rules || saved.rules,
      }));
      setHeader((prev) => ({ ...prev, status: saved.status }));
      setStatusConfirm(false);
      toast.success(
        next === "DEPRECATED"
          ? "Rule set deprecated and hidden from meet setup"
          : "Rule set active again",
      );
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const resetOrder = () => updateRows(defaultOrder(rows, typesByKey));

  const previewLines = [pinned?.text, ...rows.map((r) => r.text)].filter(
    Boolean,
  );
  const groupName = group?.name || `Group ${ruleGroupId}`;
  const pageTitle = isNew
    ? copyFrom
      ? "New rule set (copy)"
      : "New rule set"
    : `Rule set · ${detail?.ruleName ?? ""}`;

  const copySet = useCallback(
    () =>
      detail
        ? {
            id: detail.id,
            ruleGroupId: detail.ruleGroupId,
            ruleName: detail.ruleName,
          }
        : null,
    [detail],
  );

  return (
    <Box className={classes.page}>
      <Container maxWidth="xl">
        <Breadcrumbs
          items={[
            { label: "Home", to: "/" },
            {
              label: "Rules",
              to: resolveReturnTo(location, rulesPath.groups()),
            },
            {
              label: groupName,
              to: resolveReturnTo(location, rulesPath.sets(ruleGroupId)),
            },
            {
              label: isNew
                ? "New rule set"
                : detail?.ruleName || `Set ${setId}`,
            },
          ]}
        />
        <Box className={classes.header}>
          <Box className={classes.titleRow}>
            <Typography component="h1" className={classes.title}>
              {pageTitle}
            </Typography>
            {!isNew && <StatusChip status={header.status} />}
            {dirty && <span className={classes.muted}>Unsaved changes</span>}
          </Box>
          <Button className={classes.touch} variant="outlined" onClick={leave}>
            Back to rule sets
          </Button>
        </Box>

        {loadError && <Alert severity="error">{loadError}</Alert>}
        {catalog.error && <Alert severity="error">{catalog.error}</Alert>}
        {loading && (
          <Box className={classes.progressWrap}>
            <CircularProgress aria-label="Loading rule set" />
          </Box>
        )}

        {!loading && !loadError && (
          <Paper>
            {saveError && (
              <Box padding="16px 20px 0">
                <Alert severity="error">
                  {saveError.message}
                  {saveError.conflictingSetId && (
                    <>
                      {" "}
                      <RouterLink
                        to={rulesPath.set(saveError.conflictingSetId)}
                        state={keepState(location)}
                      >
                        Open rule set {saveError.conflictingSetId}
                      </RouterLink>
                    </>
                  )}
                </Alert>
              </Box>
            )}
            {warnings.length > 0 && (
              <Box padding="16px 20px 0">
                <Alert severity="warning">
                  <ul style={{ margin: 0, paddingLeft: 20 }}>
                    {warnings.map((w) => (
                      <li key={w}>{w}</li>
                    ))}
                  </ul>
                </Alert>
              </Box>
            )}
            {group?.status === "DEPRECATED" && isNew && (
              <Box padding="16px 20px 0">
                <Alert severity="warning">
                  {groupName} is deprecated; the server refuses new sets in it
                  until it is reactivated.
                </Alert>
              </Box>
            )}

            <Box className={classes.editor}>
              <Box
                component="section"
                className={classes.editorPane}
                aria-label="Applies when"
              >
                <Typography component="h2" className={classes.paneTitle}>
                  Applies when
                </Typography>
                <HeaderForm
                  value={header}
                  onChange={updateHeader}
                  errors={headerErrors}
                  disabled={saving}
                />
                <Box marginTop="20px">
                  <ClassificationToggles
                    value={header}
                    onChange={(flags) =>
                      updateHeader({
                        ...header,
                        novice: flags.novice,
                        synchro: flags.synchro,
                        masters: flags.masters,
                      })
                    }
                    disabled={saving}
                  />
                </Box>
              </Box>

              <Box
                component="section"
                className={classes.editorPane}
                aria-label="Rules"
              >
                <Typography component="h2" className={classes.paneTitle}>
                  Rules{" "}
                  <span
                    className={classes.inlineHelp}
                    style={{ textTransform: "none" }}
                  >
                    (the order shown to coaches)
                  </span>
                </Typography>
                <RuleList
                  rows={rows}
                  pinned={pinned}
                  typesByKey={typesByKey}
                  disabled={saving}
                  onReorder={updateRows}
                  onEdit={(row) => openEdit(row, MODES.REPLACE)}
                  onEditEverywhere={(row) => openEdit(row, MODES.EVERYWHERE)}
                  onRemove={removeRow}
                />
                <Box
                  marginTop="12px"
                  display="flex"
                  style={{ gap: 8 }}
                  flexWrap="wrap"
                >
                  <Button
                    variant="contained"
                    color="primary"
                    className={classes.touch}
                    onClick={openAdd}
                    disabled={saving}
                  >
                    Add rule
                  </Button>
                  <Button
                    variant="outlined"
                    className={classes.touch}
                    onClick={resetOrder}
                    disabled={saving || rows.length < 2}
                  >
                    Reset to default order
                  </Button>
                </Box>
                <Box className={classes.preview}>
                  <div className={classes.previewLabel}>
                    Preview as shown to coaches (en-US)
                  </div>
                  {previewLines.length ? previewLines.join("\n") : "—"}
                </Box>
              </Box>
            </Box>

            <Box className={classes.footer}>
              <Typography className={classes.muted}>
                {isNew
                  ? "Not saved yet"
                  : `${auditSentence(detail?.audit)} · Used by ${
                      detail?.eventCount ?? 0
                    } event${detail?.eventCount === 1 ? "" : "s"}`}
              </Typography>
              <Box className={classes.footerActions}>
                {!isNew && (
                  <Button
                    variant="outlined"
                    className={classes.touch}
                    onClick={() => setCopying(true)}
                    disabled={saving}
                  >
                    Copy as new…
                  </Button>
                )}
                {!isNew && (
                  <Button
                    variant="outlined"
                    className={classes.touch}
                    onClick={() => setStatusConfirm(true)}
                    disabled={saving || busy}
                  >
                    {header.status === "DEPRECATED"
                      ? "Reactivate"
                      : "Deprecate"}
                  </Button>
                )}
                <Button
                  variant="contained"
                  color="primary"
                  className={classes.touch}
                  onClick={save}
                  disabled={!canSave}
                >
                  {saving ? "Saving…" : isNew ? "Create rule set" : "Save"}
                </Button>
              </Box>
            </Box>
          </Paper>
        )}
      </Container>

      <AddRuleDialog
        open={dialog.open}
        mode={dialog.mode}
        initial={dialog.initial}
        setName={header.ruleName || "this rule set"}
        totalDives={total}
        onClose={() =>
          setDialog({
            open: false,
            mode: MODES.ADD,
            initial: null,
            rowKey: null,
          })
        }
        onApply={applyDialog}
      />

      <CopySetDialog
        open={copying}
        set={copySet()}
        onClose={() => setCopying(false)}
      />

      <ConfirmAction
        open={statusConfirm}
        title={
          header.status === "DEPRECATED"
            ? "Reactivate this rule set?"
            : "Deprecate this rule set?"
        }
        text={
          header.status === "DEPRECATED"
            ? "The rule set will be offered in meet setup again."
            : "The rule set will no longer be offered in meet setup. Events already using it keep it."
        }
        confirmLabel={
          header.status === "DEPRECATED" ? "Reactivate" : "Deprecate"
        }
        busy={busy}
        onConfirm={toggleStatus}
        onCancel={() => setStatusConfirm(false)}
      />
    </Box>
  );
};

export default RuleSetEditor;
